import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { interpolate } from '../../cms';
import { usePet } from '../../pets';
import { usePayment } from '../../payment';
import { ApiResult } from '../../api';
import { RedirectToLogin } from '../../router';
import { useToast, TOAST_CLIENT_ERROR_MESSAGE } from '../../Toast';
import { useSessionCustomer, useAddress } from '../../customer';
import { useSubscription } from '../../subscriptions';
import { useProduct } from '../../products';
import { costFormatter } from '../../format';
import { PlanSummary } from '../PlanSummary';
import { getPetInfo } from '../AccountContainer/dataParsers';
import { useReporter } from '../../reporter';
import { PAYMENT_ERROR_MESSAGE } from '../constants';
import { LoadingView } from '../../components';
import { useOffer, REACTIVATION_CODE_TYPE } from '../../offer';
import { planReactivateClicked, planSummaryReactivation } from './events';

export const ReactivationPlanSummaryContainer = ({
  cancelBtnLabel,
  period,
  headline,
  productName,
  subHeadingText,
  confirmationText,
  pupInfoTarget,
  freshRecipesTarget,
  bakedRecipesTarget,
  mealPrepTarget,
  paymentTarget,
  deliveryTarget,
  cancelTarget,
  onSuccess,
}) => {
  const router = useRouter();
  const reporter = useReporter();
  const offer = useOffer();
  const petId = router.query.pet_id;
  const { showToast } = useToast();
  const {
    customer,
    isActive,
    isLoading: isCustomerLoading,
  } = useSessionCustomer();
  const { payment, isLoading: isPaymentLoading } = usePayment(customer?.id);
  const { address, isLoading: isAddressLoading } = useAddress(customer?.id);
  const { pet, isLoading: isPetLoading } = usePet(petId);
  const {
    isLoading: isSubscriptionLoading,
    reactivatePlan,
    subscription,
  } = useSubscription(petId);
  const { product, isLoading: isProductLoading } = useProduct(
    subscription?.sku,
    offer.category === REACTIVATION_CODE_TYPE ? { couponCode: offer.code } : {},
  );
  const [errorMessage, setErrorMessage] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isLoading =
    isPetLoading ||
    isCustomerLoading ||
    isSubscriptionLoading ||
    isProductLoading ||
    isAddressLoading ||
    isPaymentLoading;

  let paymentInfo = [];

  useEffect(() => {
    if (!isLoading) reporter.tag(planSummaryReactivation());
  }, [reporter, isLoading]);

  if (!isCustomerLoading && !isActive) {
    return <RedirectToLogin />;
  }

  if (isLoading) {
    return <LoadingView />;
  }

  const pupInfo = [`${pet.name} is a ${getPetInfo(pet)}`];
  const recipeInfo = product.variants[0].recipes;
  const totalAmount = costFormatter(product.variants[0].price_per_week);
  const discountedAmountValue = costFormatter(
    product.variants[0].discounted_price_per_week,
  );
  const discountedPeriodAmount =
    discountedAmountValue !== totalAmount ? discountedAmountValue : null;

  if (payment) {
    paymentInfo = [
      `${payment.brand}`,
      `Ending in ****${payment.last_four_digits}`,
    ];
  }

  const deliveryInfo = [
    `${address.first_name} ${address.last_name}`,
    `${address.address1}, ${address.address2}`,
    `${address.city}, ${address.state} ${address.zip_code}`,
  ];

  const onSubmit = async () => {
    setIsSubmitting(true);
    const code = offer.category === REACTIVATION_CODE_TYPE ? offer.code : null;
    const result = await reactivatePlan(code);
    reporter.tag(planReactivateClicked());
    ApiResult.match(result, {
      success: () => {
        onSuccess();
        router.push('/account');
      },
      error: {
        server: error => {
          showToast({
            status: 'error',
            message:
              'We weren’t able to complete your request, please refresh the page and try again.',
            headline: 'Error Message',
          });
        },
        client: error => {
          reporter.error(error.original);
          showToast({
            status: 'error',
            message: TOAST_CLIENT_ERROR_MESSAGE,
            headline: 'Error Message',
          });
        },
        payment: () => setErrorMessage(PAYMENT_ERROR_MESSAGE),
      },
    });
    ApiResult.ifError(result, () => setTimeout(() => setIsSubmitting(false)));
  };
  return (
    <PlanSummary
      confirmationButtonText="Reactivate Subscription"
      name={customer.first_name}
      headline={interpolate(headline, { pupName: pet.name })}
      subheadingText={subHeadingText}
      pupsInfo={pupInfo}
      recipesInfo={recipeInfo}
      paymentInfo={paymentInfo}
      deliveryInfo={deliveryInfo}
      confirmationText={confirmationText}
      cancelButtonLabel={cancelBtnLabel}
      boxheadline={interpolate(productName, {
        pupName: pet.name,
        frequency: product.weeks_of_food,
      })}
      totalPacks={Number(product.variants[0].tray_count)}
      totalBags={Number(product.variants[0].bag_count)}
      discountedPeriodAmount={discountedPeriodAmount}
      totalAmount={totalAmount}
      period={period}
      offerCategory={REACTIVATION_CODE_TYPE}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
      reactivationMode
      pupInfoTarget={pupInfoTarget}
      freshRecipesTarget={freshRecipesTarget}
      dryRecipesTarget={bakedRecipesTarget}
      mealPrepTarget={mealPrepTarget}
      paymentTarget={paymentTarget}
      deliveryTarget={deliveryTarget}
      errorMessage={errorMessage}
      cancelButtonTarget={cancelTarget}
    />
  );
};

ReactivationPlanSummaryContainer.displayName =
  'ReactivationPlanSummaryContainer';

ReactivationPlanSummaryContainer.propTypes = {
  headline: PropTypes.string.isRequired,
  subHeadingText: PropTypes.string.isRequired,
  confirmationText: PropTypes.string.isRequired,
  cancelBtnLabel: PropTypes.string.isRequired,
  period: PropTypes.string.isRequired,
  productName: PropTypes.string.isRequired,
  cancelTarget: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    .isRequired,
  pupInfoTarget: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    .isRequired,
  freshRecipesTarget: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    .isRequired,
  bakedRecipesTarget: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    .isRequired,
  mealPrepTarget: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    .isRequired,
  paymentTarget: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    .isRequired,
  deliveryTarget: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    .isRequired,
  onSuccess: PropTypes.func.isRequired,
};
